import type { CopyJobState } from "~/classes/job/CopyJob"
import type { DownloadJob } from "~/classes/job/DownloadJob"
import type { UploadJobState } from "~/classes/job/UploadJob"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { BrowserCopyJob, CopyJob, DesktopCopyJob } from "~/classes/job/CopyJob"
import { BrowserDownloadJob, DesktopDownloadJob } from "~/classes/job/DownloadJob"
import { BrowserUploadJob, UploadJob } from "~/classes/job/UploadJob"

class Jobs extends GlobalComposable<{
  jobs: Record<string, JobState>
}> {
  override defaultState = () => ({ jobs: {} })

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("fileCreateResponse", this.onFileCreated)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("fileCreateResponse", this.onFileCreated)
  }

  isJobInProgress = (transferId: string) => {
    return isDefined(this.state.value.jobs[transferId])
      && !["complete", "failed"].includes(this.state.value.jobs[transferId].status)
  }

  anyJobsInProgress = computed(() => {
    for (const jobId in this.state.value.jobs)
      if (this.isJobInProgress(jobId))
        return true
    return false
  })

  progressDetails = (transferId: string) => {
    const job = this.state.value.jobs[transferId]
    if (!job)
      return { finishedBytes: 0, successBytes: 0, failedBytes: 0, totalBytes: 0 }
    if (job.type === "upload")
      return this.uploadJob(transferId)!.progressDetails()
    else if (job.type === "download")
      return (job as DownloadJob).progressDetails()
    else if (job.type === "copy")
      return this.copyJob(transferId)!.progressDetails()
    else
      throw new Error("Unknown job type")
  }

  uploadJob = (transferId: string) => {
    if (!isDefined(this.state.value.jobs[transferId]))
      return
    if (this.state.value.jobs[transferId].type !== "upload")
      throw new Error("Not an upload job")
    return new UploadJob(toRef(this.state.value.jobs, transferId) as Ref<UploadJobState>)
  }

  downloadJob = (transferId: string) => {
    if (!isDefined(this.state.value.jobs[transferId]))
      return
    if (this.state.value.jobs[transferId].type !== "download")
      throw new Error("Not a download job")
    return toRef(this.state.value.jobs, transferId) as Ref<DownloadJob>
  }

  copyJob = (transferId: string) => {
    if (!isDefined(this.state.value.jobs[transferId]))
      return
    if (this.state.value.jobs[transferId].type !== "copy")
      throw new Error("Not a copy job")
    return new CopyJob(toRef(this.state.value.jobs, transferId) as Ref<CopyJobState>)
  }

  /**
   * Do not use this method for UI level things, this is just for internal cleanup.
   * Instead, use `clearNotificationById`.
   */
  clearJobById = (transferId: string) => {
    delete this.state.value.jobs[transferId]
  }

  clearAllJobs = () => {
    this.state.value.jobs = {}
  }

  /** TODO: implement cancellation */
  cancelJob = (transferId: string) => {
    delete this.state.value.jobs[transferId]
  }

  // On web, we must unpack all the files on the client, as the DataTransfer is not,
  // ironically, a transferable object that we can work with in the web worker.
  //
  // 1. Iterate over all items, and assign ids to them, and try to upload them each to
  //    the sync sandbox worker
  // 2. Once we have a set of file datagrams (one per upload, each with a client id,
  //    client uri, and object-specific metadata), we can send the whole message to
  //    the upload manager in the cache worker
  onWebFilesDropped = async (
    transfer: DataTransfer,
    destination: InterfaceFile,
  ) => {
    const { openNotificationTray } = useNotificationTray()

    const job: UploadJobState = {
      transferId: generateWorkerRequestId(),
      folderId: destination.fileId,
      status: "counting",
      type: "upload",
      destination,
      timestamp: Date.now(),
      files: {},
    }
    this.state.value.jobs[job.transferId] = job
    const browserJob = new BrowserUploadJob(toRef(this.state.value.jobs, job.transferId) as Ref<UploadJobState>)
    browserJob.startUpload(destination, transfer)
    openNotificationTray()
  }

  onUploadJobStarted = (status: UploadStartedStatusDatagram) => {
    const job = this.uploadJob(status.transferId)
    if (!isDefined(job))
      return this.warn("No job on client, likely on another tab?", status)
    job.started(status)
  }

  onUploadFileProgressStatus = (status: UploadFileProgressStatusDatagram) => {
    const job = this.uploadJob(status.transferId)
    if (!isDefined(job))
      return this.warn("No job on client, likely on another tab?", status)
    job.fileUpdated(status)
  }

  onUploadFileFailedStatus = (status: UploadFileFailedStatusDatagram) => {
    const job = this.uploadJob(status.transferId)
    if (!isDefined(job))
      return this.warn("No job on client, likely on another tab?", status)
    job.fileFailed(status)
  }

  onUploadJobFinished = (status: UploadFinishedStatusDatagram) => {
    const { onJobCompleted } = useNotificationTray()

    const job = this.uploadJob(status.transferId)
    if (!isDefined(job))
      return this.warn("No job on client, likely on another tab?", status)
    job.finished(status)
    // TODO: determine between failed and finished, showing different views
    // in either instance.
    onJobCompleted(job.state.value.transferId)
  }

  onDesktopFilesDropped = async (paths: string[], folder?: InterfaceFile) => {
    const { state: myself } = useSelf()
    const { currentRootFolder } = useNavigation()
    const { uploadItems } = useTauri()

    const userId = myself.value.user?.userId
    if (!userId)
      throw new Error("No user for upload")
    const destination = folder ?? currentRootFolder.value
    if (!destination)
      throw new Error("No folder to upload to")

    const job: UploadJobState = {
      transferId: generateWorkerRequestId(),
      folderId: destination.fileId,
      status: "preparing",
      type: "upload",
      destination,
      timestamp: Date.now(),
      files: {},
    }
    this.state.value.jobs[job.transferId] = job

    return uploadItems(paths, userId, job.folderId, job.transferId)
  }

  onFileCreated = (response: FileCreateResponseDatagram) => {
    // we tell the upload job if the file creation was successful or not
    // TODO: this should just be a status update, not a response
    for (let job of Object.values(this.state.value.jobs)) {
      if (job.type === "upload") {
        job = job as UploadJobState
        if (response.fileId in job.files) {
          if (isDefined(response.error))
            job.files[response.fileId].status = "failed"
          else
            job.files[response.fileId].status = "complete"
        }
      }
    }
  }

  copyFiles = (sources: Record<string, InterfaceFile>, destination: InterfaceFile) => {
    const { openNotificationTray } = useNotificationTray()

    const job: CopyJobState = {
      transferId: generateWorkerRequestId(),
      folderId: destination.fileId,
      status: "preparing",
      type: "copy",
      destination,
      timestamp: Date.now(),
      sources,
      files: {},
    }

    this.state.value.jobs[job.transferId] = job

    let copyJob
    if (isDesktop()) {
      copyJob = new DesktopCopyJob(toRef(this.state.value.jobs, job.transferId) as Ref<CopyJobState>)
    }
    else {
      copyJob = new BrowserCopyJob(toRef(this.state.value.jobs, job.transferId) as Ref<CopyJobState>)
    }

    copyJob.startCopy()
    openNotificationTray()
  }

  onCopyJobStarted = (status: CopyStartedStatusDatagram) => {
    const job = this.copyJob(status.transferId)
    if (!isDefined(job))
      return this.warn("No job on client, likely on another tab?", status)
    job.started(status)
  }

  onCopyFileProgressStatus = (status: CopyFileProgressStatusDatagram) => {
    const job = this.copyJob(status.transferId)
    if (!isDefined(job))
      return this.warn("No job on client, likely on another tab?", status)
    job.fileUpdated(status)
  }

  onCopyFileFailedStatus = (status: CopyFileFailedStatusDatagram) => {
    const job = this.copyJob(status.transferId)
    if (!isDefined(job))
      return this.warn("No job on client, likely on another tab?", status)
    job.fileFailed(status)
  }

  onCopyJobFinished = (status: CopyFinishedStatusDatagram) => {
    const { onJobCompleted } = useNotificationTray()

    const job = this.copyJob(status.transferId)
    if (!isDefined(job))
      return this.warn("No job on client, likely on another tab?", status)
    job.finished(status)
    // TODO: determine between failed and finished, showing different views
    // in either instance.
    onJobCompleted(job.state.value.transferId)
  }

  downloadFiles = (sources: Record<string, InterfaceFile>) => {
    if (isDesktop())
      return this.downloadFilesDesktopWithDialog(sources)
    else
      return this.downloadFilesOnWeb(sources)
  }

  onDownloadJobStarted = (status: DownloadStartedStatusDatagram) => {
    if (!this.state.value.jobs[status.transferId]) {
      return console.error("Download job not found", status)
    }
    const job = this.state.value.jobs[status.transferId] as DownloadJob
    job.started(status)
  }

  onDownloadJobProgressStatus = (status: DownloadProgressStatusDatagram) => {
    if (!this.state.value.jobs[status.transferId]) {
      return console.error("Download job not found", status)
    }
    const job = this.state.value.jobs[status.transferId] as DownloadJob
    job.updated(status)
  }

  onDownloadFileProgressStatus = (status: DownloadFileProgressStatusDatagram) => {
    const job = this.state.value.jobs[status.transferId] as DownloadJob
    if (!job)
      return console.warn("No job on client", status)
    job.fileUpdated(status)
  }

  onDownloadJobFinished = (status: DownloadFinishedStatusDatagram) => {
    const { onJobCompleted } = useNotificationTray()

    if (!this.state.value.jobs[status.transferId]) {
      return console.error("Download job not found", status)
    }
    const job = this.state.value.jobs[status.transferId] as DownloadJob
    job.finished(status)

    // TODO: determine between failed and finished, showing different views
    // in either instance.
    onJobCompleted(job.transferId)
  }

  downloadFilesDesktopWithDialog = (sources: Record<string, InterfaceFile>) => {
    const { openNotificationTray } = useNotificationTray()

    const transferId = generateWorkerRequestId()
    const job = new DesktopDownloadJob({
      transferId,
      sources,
    })
    this.state.value.jobs[transferId] = job
    job.startDownload()
    openNotificationTray()
  }

  /** Downloads the files as a zip if multiple, else downloads as one */
  downloadFilesOnWeb = (sources: Record<string, InterfaceFile>) => {
    const { openNotificationTray } = useNotificationTray()

    const transferId = generateWorkerRequestId()
    const job = new BrowserDownloadJob({
      transferId,
      sources,
    })
    this.state.value.jobs[transferId] = job

    job.startDownload()
    openNotificationTray()
  }
}

const jobs = new Jobs("jobs", theme.colors.amber.hex)
export default function () {
  return jobs.use()
}
